import {defineStore} from 'pinia'
import axios, {AxiosError, AxiosRequestConfig} from 'axios'
import {useAuthStore} from '../AuthStore/useAuthStore'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import {IBaseApiRequestInterfaces} from '@toolify/server/src/types/IBaseApiRequestInterfaces'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {IApiRequestStoreState} from '@toolify/client/src/stores/ApiRequestStore/types/IApiRequestStoreState'
import {IApiRequestQueueItem} from '@toolify/client/src/stores/ApiRequestStore/types/IApiRequestQueueItem'
import {ApiRequestQueueItemStatus} from '@toolify/client/src/stores/ApiRequestStore/enum/ApiRequestQueueItemStatus'
import {IApiStoreRequestOptions} from '@toolify/client/src/stores/ApiRequestStore/types/IApiStoreRequestOptions'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {jsonCompressionService} from '../../modules/api/singletons/JsonCompressionService'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {i18n} from '@toolify/client/src/setup/i18n'
import {showPolicyLimitExceededToast} from '@toolify/client/src/modules/billing/helpers/showPolicyLimitExceededToast'
import {getWorkspaceBillingBlockedMessage} from '@toolify/client/src/modules/billing/helpers/getWorkspaceBillingBlockedMessage'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {getNativeFilePath} from '@toolify/client/src/plugins/FilePickerPlugin'
import {
  getNativeUploadFailure,
  isFileTransferAvailable,
  uploadFileNatively,
} from '@toolify/client/src/plugins/FileTransferPlugin'
import {getRuntimeEnv} from '@toolify/client/src/stores/AdminRuntimeStore/functions/getRuntimeEnv'
import {isGodModeEnabled} from '@toolify/client/src/stores/AdminRuntimeStore/functions/isGodModeEnabled'
import {v4 as uuidv4} from 'uuid'
import {getJitteredBackoffDelayMs} from '@toolify/client/src/modules/api/functions/getJitteredBackoffDelayMs'
import {isIdempotentRequestMethod} from '@toolify/client/src/modules/api/functions/isIdempotentRequestMethod'
import {isApiReachable} from '@toolify/client/src/modules/api/functions/isApiReachable'
import {getApiErrorReferenceText} from '@toolify/client/src/modules/api/functions/getApiErrorReferenceText'

const MAX_SIMULTANEOUS_REQUESTS = 10
const MAX_TOKEN_RETRY_COUNT = 3
const MAX_RATE_LIMIT_RETRY_COUNT = 3
const MAX_RATE_LIMIT_RETRY_SECONDS = 60
const RATE_LIMIT_RETRY_JITTER_MS = 1000
const MAX_NETWORK_RETRY_COUNT = 2
const NETWORK_RETRY_BASE_DELAY_MS = 1000
const NETWORK_RETRY_MAX_DELAY_MS = 8000
const CONNECTIVITY_CHECK_TIMEOUT_MS = 10 * 1000
const CONNECTIVITY_CHECK_BASE_DELAY_MS = 1000
const CONNECTIVITY_CHECK_MAX_DELAY_MS = 15 * 1000
const ERROR_REFERENCE_TOAST_DURATION_MS = 10 * 1000
const REQUEST_ID_HEADER = 'X-Request-Id'
const messages = useMessages()

interface IApiErrorResponseData {
  error?: {
    name: KnownError
    message: string
    extra: unknown
    requestId?: string
  }
}

interface INativeUploadRequestParams {
  url: string
  path: string
  mimeType: string
  fileKey: string
  params: Record<string, string>
}

// Files picked through the native picker above the webview-safe size carry an
// on-disk path instead of bytes; those uploads stream natively (FileTransfer)
// instead of going through an in-memory XHR body.
function extractNativeUploadRequest(url: string, config?: AxiosRequestConfig): INativeUploadRequestParams | null {
  if (!(config?.data instanceof FormData) || !isFileTransferAvailable()) {
    return null
  }
  let fileEntry: {
    fileKey: string
    file: File
    path: string
  } | null = null
  const params: Record<string, string> = {}
  for (const [key, value] of config.data.entries()) {
    if (value instanceof File) {
      const nativePath = getNativeFilePath(value)
      if (!nativePath || fileEntry) {
        return null
      }
      fileEntry = {
        fileKey: key,
        file: value,
        path: nativePath,
      }
    } else {
      params[key] = String(value)
    }
  }
  if (!fileEntry) {
    return null
  }
  return {
    url,
    path: fileEntry.path,
    mimeType: fileEntry.file.type,
    fileKey: fileEntry.fileKey,
    params,
  }
}

// The native transfer cannot reliably deliver multipart text fields (iOS drops
// them, Android mangles them), so they travel in this base64-JSON header and
// MultipartFormService reads them server-side.
function encodeNativeUploadFields(fields: Record<string, string>): string {
  const bytes = new TextEncoder().encode(JSON.stringify(fields))
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
}

async function runNativeUploadRequest<TResult>(
  request: INativeUploadRequestParams,
  buildHeaders: () => Promise<Record<string, string>>,
  onUploadProgress: AxiosRequestConfig['onUploadProgress'],
): Promise<TResult> {
  let responseBody: string | undefined
  try {
    responseBody = await uploadFileNatively({
      url: request.url,
      path: request.path,
      mimeType: request.mimeType,
      fileKey: request.fileKey,
      async getHeaders() {
        return {
          ...await buildHeaders(),
          ...(Object.keys(request.params).length
            ? {'X-Native-Upload-Fields': encodeNativeUploadFields(request.params)}
            : {}),
          'X-Request-Compression': 'false',
        }
      },
      onProgress: onUploadProgress
        ? (progressEvent) => onUploadProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          lengthComputable: progressEvent.lengthComputable,
          bytes: progressEvent.loaded,
        })
        : undefined,
    })
  } catch (error) {
    const failure = getNativeUploadFailure(error)
    if (failure.httpStatus) {
      let parsedBody: unknown
      try {
        parsedBody = failure.body ? JSON.parse(failure.body) : null
      } catch {
        parsedBody = null
      }
      throw new AxiosError(
        failure.message,
        String(failure.httpStatus),
        undefined,
        {},
        {
          status: failure.httpStatus,
          data: parsedBody,
        } as never,
      )
    }
    throw new AxiosError(failure.message, AxiosError.ERR_NETWORK, undefined, {})
  }
  const parsedResponse = responseBody ? JSON.parse(responseBody) as {data: TResult} : {data: undefined as TResult}
  return parsedResponse.data
}

export const useApiRequestStore = defineStore('apiRequest', {
  state() {
    return {
      requestQueueItems: [],
      handleRequestQueueTimeout: null,
      isErrorReferenceToastVisible: false,
      rateLimitedUntil: null,
      isOffline: false,
      connectivityCheckAttempt: 0,
      connectivityCheckPromise: null,
      connectivityRecheckTimeout: null,
    } as IApiRequestStoreState
  },
  actions: {
    initialize() {
      window.addEventListener('online', this.onOnline)
    },

    dispose() {
      window.removeEventListener('online', this.onOnline)
      clearTimeout(this.connectivityRecheckTimeout)
      this.connectivityRecheckTimeout = null
    },

    onOnline() {
      this.connectivityCheckAttempt = 0
      void this.probeConnectivity()
    },

    async probeConnectivity(): Promise<boolean> {
      if (!this.connectivityCheckPromise) {
        this.connectivityCheckPromise = isApiReachable(getRuntimeEnv().ApiHost, CONNECTIVITY_CHECK_TIMEOUT_MS)
          .then((isReachable) => {
            if (isReachable) {
              this.markApiReachable()
            } else {
              this.markApiUnreachable()
            }
            return isReachable
          })
          .finally(() => {
            this.connectivityCheckPromise = null
          })
      }
      return await this.connectivityCheckPromise
    },

    async checkConnectivity(): Promise<boolean> {
      if (this.isOffline) {
        return false
      }
      return await this.probeConnectivity()
    },

    markApiReachable() {
      clearTimeout(this.connectivityRecheckTimeout)
      this.connectivityRecheckTimeout = null
      this.connectivityCheckAttempt = 0
      if (!this.isOffline) {
        return
      }
      this.isOffline = false
      this.requestQueueItems = this.requestQueueItems.map((item) => {
        if (item.status !== ApiRequestQueueItemStatus.Failed) {
          return item
        }
        return {
          ...item,
          status: ApiRequestQueueItemStatus.Queued,
          nextAttemptAt: null,
        }
      })
      this.handleRequestQueue()
    },

    markApiUnreachable() {
      this.isOffline = true
      clearTimeout(this.connectivityRecheckTimeout)
      const recheckDelayMs = getJitteredBackoffDelayMs(
        this.connectivityCheckAttempt,
        CONNECTIVITY_CHECK_BASE_DELAY_MS,
        CONNECTIVITY_CHECK_MAX_DELAY_MS,
      )
      this.connectivityCheckAttempt++
      this.connectivityRecheckTimeout = setTimeout(() => {
        this.connectivityRecheckTimeout = null
        void this.probeConnectivity()
      }, recheckDelayMs)
    },

    async get<Interfaces extends IBaseApiRequestInterfaces>(
      path: string,
      config?: AxiosRequestConfig,
      options?: IApiStoreRequestOptions,
    ): Promise<IToJson<Interfaces['response']>> {
      return await this.request<Interfaces>('get', path, config, options)
    },

    async delete<Interfaces extends IBaseApiRequestInterfaces>(
      path: string,
      config?: AxiosRequestConfig,
      options?: IApiStoreRequestOptions,
    ): Promise<IToJson<Interfaces['response']>> {
      return await this.request<Interfaces>('delete', path, config, options)
    },

    async post<Interfaces extends IBaseApiRequestInterfaces>(
      path: string,
      config?: AxiosRequestConfig,
      options?: IApiStoreRequestOptions,
    ): Promise<IToJson<Interfaces['response']>> {
      return await this.request<Interfaces>('post', path, config, options)
    },

    async put<Interfaces extends IBaseApiRequestInterfaces>(
      path: string,
      config?: AxiosRequestConfig,
      options?: IApiStoreRequestOptions,
    ): Promise<IToJson<Interfaces['response']>> {
      return await this.request<Interfaces>('put', path, config, options)
    },

    async patch<Interfaces extends IBaseApiRequestInterfaces>(
      path: string,
      config?: AxiosRequestConfig,
      options?: IApiStoreRequestOptions,
    ): Promise<IToJson<Interfaces['response']>> {
      return await this.request<Interfaces>('patch', path, config, options)
    },

    async postBlob(
      path: string,
      config?: AxiosRequestConfig,
      options?: IApiStoreRequestOptions,
    ): Promise<Blob> {
      const response = await this.request<IBaseApiRequestInterfaces>('post', path, {
        ...(config ? config : {}),
        responseType: 'blob',
      }, options)
      return response as unknown as Blob
    },

    addRequestToQueue<T>(item: IApiRequestQueueItem<T>) {
      this.requestQueueItems.push(item as IApiRequestQueueItem<unknown>)
      this.requestQueueItems = this.requestQueueItems.sort((a, b) => {
        return b.options.priority - a.options.priority
      })
      this.handleRequestQueue()
    },

    handleRequestQueue() {
      clearTimeout(this.handleRequestQueueTimeout)
      this.handleRequestQueueTimeout = null
      if (this.isOffline) {
        return
      }
      const now = Date.now()
      const isItemDue = (item: IApiRequestQueueItem<unknown>) => {
        return item.status === ApiRequestQueueItemStatus.Queued && (!item.nextAttemptAt || item.nextAttemptAt <= now)
      }
      for (const item of this.requestQueueItems.filter((item) => isItemDue(item) && item.options?.bypassQueueLimits)) {
        this.startQueueItem(item)
      }
      const isRateLimited = !!this.rateLimitedUntil && Number(this.rateLimitedUntil) > now
      if (!isRateLimited) {
        const processingItemsCount = this.requestQueueItems.filter(
          (item) => item.status === ApiRequestQueueItemStatus.Processing && !item.options?.bypassQueueLimits,
        ).length
        const freeSlotsCount = MAX_SIMULTANEOUS_REQUESTS - processingItemsCount
        const itemsToStart = this.requestQueueItems
          .filter((item) => isItemDue(item) && !item.options?.bypassQueueLimits)
          .slice(0, Math.max(freeSlotsCount, 0))
        for (const item of itemsToStart) {
          this.startQueueItem(item)
        }
      }
      this.scheduleRequestQueueWakeUp(now)
    },

    scheduleRequestQueueWakeUp(now: number) {
      const queuedItems = this.requestQueueItems.filter((item) => item.status === ApiRequestQueueItemStatus.Queued)
      const wakeUpTimes = queuedItems
        .map((item) => item.nextAttemptAt)
        .filter((nextAttemptAt) => !!nextAttemptAt && nextAttemptAt > now)
      if (queuedItems.length && this.rateLimitedUntil && Number(this.rateLimitedUntil) > now) {
        wakeUpTimes.push(Number(this.rateLimitedUntil))
      }
      if (!wakeUpTimes.length) {
        return
      }
      this.handleRequestQueueTimeout = setTimeout(() => {
        this.handleRequestQueue()
      }, Math.min(...wakeUpTimes) - now)
    },

    startQueueItem(item: IApiRequestQueueItem<unknown>) {
      this.setQueueItemStatus(item.id, ApiRequestQueueItemStatus.Processing)
      this.handleQueueItem(item)
    },

    async retryQueueItemAfterAuthRecovery(
      item: IApiRequestQueueItem<unknown>,
      recoverAuth: () => Promise<void>,
    ): Promise<boolean> {
      const tokenRetryCount = item.tokenRetryCount ?? 0
      if (tokenRetryCount >= MAX_TOKEN_RETRY_COUNT) {
        return false
      }
      try {
        await recoverAuth()
      } catch (authError) {
        item.onReject(authError)
        this.deleteQueueItem(item.id)
        return true
      }
      this.updateQueueItem(item.id, {
        status: ApiRequestQueueItemStatus.Queued,
        tokenRetryCount: tokenRetryCount + 1,
      })
      this.handleRequestQueue()
      return true
    },

    retryQueueItemAfterRateLimit(item: IApiRequestQueueItem<unknown>, errorExtra: unknown): boolean {
      const retryAfterSeconds = Number((errorExtra as {retryAfterSeconds?: unknown} | null)?.retryAfterSeconds)
      const rateLimitRetryCount = item.rateLimitRetryCount ?? 0
      if (
        item.options?.bypassQueueLimits
        || !Number.isFinite(retryAfterSeconds)
        || retryAfterSeconds > MAX_RATE_LIMIT_RETRY_SECONDS
        || rateLimitRetryCount >= MAX_RATE_LIMIT_RETRY_COUNT
      ) {
        return false
      }
      const retryDate = new Date(Date.now() + Math.max(retryAfterSeconds, 1) * 1000 + Math.random() * RATE_LIMIT_RETRY_JITTER_MS)
      if (!this.rateLimitedUntil || this.rateLimitedUntil < retryDate) {
        this.rateLimitedUntil = retryDate
      }
      this.updateQueueItem(item.id, {
        status: ApiRequestQueueItemStatus.Queued,
        rateLimitRetryCount: rateLimitRetryCount + 1,
      })
      this.handleRequestQueue()
      return true
    },

    async handleQueueItem(item: IApiRequestQueueItem<unknown>): Promise<void> {
      const authStore = useAuthStore()
      
      try {
        const response = await this.makeRequest(
          item.method,
          item.path,
          item.config,
          item.requestId,
        )
        item.onResolve(response)
        this.deleteQueueItem(item.id)
        if (this.isOffline) {
          this.markApiReachable()
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          item.onReject(error)
          this.deleteQueueItem(item.id)
          return
        }
        if (error instanceof AxiosError) {
          if (!error.response && error.request) {
            await this.handleNetworkFailure(item, error)
            return
          }
          const responseData = error.response ? await this.resolveErrorResponseData(error) : null
          if (responseData) {
            const res = responseData
            if (res.error) {
              if (res.error.name === KnownError.MISSING_TOKEN) {
                // eslint-disable-next-line no-console
                console.log('MISSING_TOKEN')
                if (await this.retryQueueItemAfterAuthRecovery(item, () => authStore.createSession())) {
                  return
                }
              }
              if (res.error.name === KnownError.TOKEN_EXPIRED) {
                // eslint-disable-next-line no-console
                console.log('TOKEN_EXPIRED')
                if (await this.retryQueueItemAfterAuthRecovery(item, () => authStore.renewToken())) {
                  return
                }
              }
              if (res.error.name === KnownError.INVALID_TOKEN) {
                // eslint-disable-next-line no-console
                console.log('INVALID_TOKEN')
                await authStore.removeTokenWithExpiryDate()
                authStore.$patch({
                  isLoggedIn: false,
                })
                item.onReject(this.createApiException(res.error, error.response.status, item.requestId))
                return this.deleteQueueItem(item.id)
              }
              if (res.error.name === KnownError.NoPermissions) {
                // eslint-disable-next-line no-console
                console.log('NoPermissions')
                const currentUser = authStore.currentEntity?.entityType === EntityType.User
                  ? authStore.currentEntity.payload as IUserResponseInstance
                  : null
                const shouldShowRawMessage = !import.meta.env.PROD || currentUser?.isSuperAdmin
                // a burst of refused requests folds into one card with ×N
                if(shouldShowRawMessage && res.error.message) {
                  messages.error(res.error.message, {
                    title: i18n.global.t('general.noPermissions'),
                    collapseKey: `permission.denied:${res.error.message}`,
                  })
                } else {
                  messages.error(i18n.global.t('general.noPermissions'), {
                    collapseKey: 'permission.denied',
                  })
                }
              }
              if (res.error.name === KnownError.INVALID_REQUEST) {
                // eslint-disable-next-line no-console
                console.log('INVALID_REQUEST')
                const currentUser = authStore.currentEntity?.entityType === EntityType.User
                  ? authStore.currentEntity.payload as IUserResponseInstance
                  : null
                if((!import.meta.env.PROD || currentUser?.isSuperAdmin) && res.error.message) {
                  messages.error(res.error.message, {
                    title: i18n.global.t('notifications.messages.common.requestRejected'),
                    collapseKey: `api.invalidRequest:${res.error.message}`,
                  })
                }
                // this is to catch validation errors
                // eslint-disable-next-line no-console
                console.error(res.error,item)
              }
              if (res.error.name === KnownError.POLICY_LIMIT_EXCEEDED) {
                const apiException = this.createApiException(res.error, error.response.status, item.requestId)
                showPolicyLimitExceededToast(apiException)
                item.onReject(apiException)
                this.deleteQueueItem(item.id)
                return
              }
              const billingBlockedMessage = getWorkspaceBillingBlockedMessage(
                res.error.name,
                useWorkspaceStore().currentWorkspace?.name,
                i18n.global.t,
              )
              if (billingBlockedMessage) {
                // every blocked request of a burst folds into one card
                const collapseKey = `billing.blocked:${res.error.name}`
                if(billingBlockedMessage.action) {
                  // the user has to act (upgrade, change the payment method), so the card stays until closed
                  messages.error(billingBlockedMessage.text, {
                    duration: 0,
                    pinned: true,
                    actions: [billingBlockedMessage.action],
                    collapseKey,
                  })
                } else {
                  messages.error(billingBlockedMessage.text, {collapseKey})
                }
                item.onReject(this.createApiException(res.error, error.response.status, item.requestId))
                this.deleteQueueItem(item.id)
                return
              }
              if (
                res.error.name === KnownError.TOO_MANY_REQUESTS
                && this.retryQueueItemAfterRateLimit(item, res.error.extra)
              ) {
                return
              }
              const apiException = this.createApiException(res.error, error.response.status, item.requestId)
              if (error.response.status >= 500) {
                this.showErrorReferenceToast(item, 'errors.unexpectedErrorWithId', apiException.requestId, res.error.extra)
              }
              item.onReject(apiException)
              this.deleteQueueItem(item.id)
              return
            }
          }
          if (error.response) {
            this.showErrorReferenceToast(item, 'errors.unexpectedErrorWithId')
          }
        }
        item.onReject(error)
        this.deleteQueueItem(item.id)
      }
    },

    async handleNetworkFailure(item: IApiRequestQueueItem<unknown>, error: AxiosError): Promise<void> {
      // A multipart upload must not be auto-retried: when only the response was lost,
      // the server may have already stored the file, so a silent re-send would create
      // a duplicate File + linking record. The user retries uploads explicitly.
      if (item.config?.data instanceof FormData) {
        item.onReject(error)
        this.deleteQueueItem(item.id)
        return
      }
      const networkRetryCount = (item.networkRetryCount ?? 0) + 1
      this.updateQueueItem(item.id, {
        status: ApiRequestQueueItemStatus.Failed,
        networkRetryCount,
        nextAttemptAt: null,
      })
      if (!await this.checkConnectivity()) {
        return
      }
      const currentItem = this.requestQueueItems.find((queueItem) => queueItem.id === item.id)
      if (currentItem?.status !== ApiRequestQueueItemStatus.Failed) {
        return
      }
      const isRetryable = isIdempotentRequestMethod(item.method) || item.options?.retryOnNetworkError
      if (isRetryable && networkRetryCount <= MAX_NETWORK_RETRY_COUNT) {
        this.updateQueueItem(item.id, {
          status: ApiRequestQueueItemStatus.Queued,
          nextAttemptAt: Date.now() + getJitteredBackoffDelayMs(
            networkRetryCount - 1,
            NETWORK_RETRY_BASE_DELAY_MS,
            NETWORK_RETRY_MAX_DELAY_MS,
          ),
        })
        this.handleRequestQueue()
        return
      }
      this.showErrorReferenceToast(item, 'errors.requestFailedWithId')
      item.onReject(error)
      this.deleteQueueItem(item.id)
    },

    createApiException(
      errorData: IApiErrorResponseData['error'],
      httpCode: number,
      requestId: string,
    ): ApiException {
      return new ApiException(
        errorData.name,
        errorData.message,
        httpCode,
        true,
        errorData.extra,
        errorData.requestId ?? requestId,
      )
    },

    showErrorReferenceToast(
      item: IApiRequestQueueItem<unknown>,
      messageKey: string,
      requestId: string = item.requestId,
      errorExtra?: unknown,
    ) {
      if (item.options?.hideErrorReferenceToast || this.isErrorReferenceToastVisible) {
        return
      }
      const referenceText = getApiErrorReferenceText(requestId, (errorExtra as {id?: unknown} | null)?.id)
      this.isErrorReferenceToastVisible = true
      messages.error(i18n.global.t(messageKey), {
        errorId: referenceText,
        duration: ERROR_REFERENCE_TOAST_DURATION_MS,
        pinned: false,
        onDismiss: () => {
          this.isErrorReferenceToastVisible = false
        },
      })
    },

    async resolveErrorResponseData(error: AxiosError): Promise<IApiErrorResponseData | null> {
      const responseData = error.response?.data
      if (!responseData) {
        return null
      }
      if (responseData instanceof Blob) {
        try {
          return JSON.parse(await responseData.text())
        } catch {
          return null
        }
      }
      return responseData as IApiErrorResponseData
    },

    deleteQueueItem(id: symbol) {
      this.requestQueueItems = this.requestQueueItems.filter(
        (item) => item.id !== id,
      )
      this.handleRequestQueue()
    },

    setQueueItemStatus(id: symbol, status: ApiRequestQueueItemStatus) {
      this.updateQueueItem(id, {status})
    },

    updateQueueItem(id: symbol, patch: Partial<IApiRequestQueueItem<unknown>>) {
      this.requestQueueItems = this.requestQueueItems.map((item) => {
        if (item.id !== id) {
          return item
        }
        return {
          ...item,
          ...patch,
        }
      })
    },

    getDefaultRequestOptions(): IApiStoreRequestOptions {
      return {
        priority: 0,
      }
    },

    requestAndGetId<Interfaces extends IBaseApiRequestInterfaces>(
      method = 'get',
      path: string,
      config?: AxiosRequestConfig,
      options?: IApiStoreRequestOptions,
    ): {
      id: symbol;
      promise: Promise<IToJson<Interfaces['response']>>;
    } {
      const id = Symbol()
      const requestId = String(config?.headers?.[REQUEST_ID_HEADER] ?? uuidv4())
      const promise: Promise<IToJson<Interfaces['response']>> = new Promise(
        (resolve, reject) => {
          const onResolve = (response: IToJson<Interfaces['response']>) => {
            resolve(response)
          }
          const onReject = (error) => {
            reject(error)
          }
          this.addRequestToQueue<IToJson<Interfaces['response']>>({
            id,
            requestId,
            method,
            path,
            config,
            options: {
              ...this.getDefaultRequestOptions(),
              ...(options ? options : {}),
            },
            status: ApiRequestQueueItemStatus.Queued,
            onResolve,
            onReject,
          })
        },
      )
      return {
        id,
        promise,
      }
    },

    request<Interfaces extends IBaseApiRequestInterfaces>(
      method = 'get',
      path: string,
      config?: AxiosRequestConfig,
      options?: IApiStoreRequestOptions,
    ): Promise<IToJson<Interfaces['response']>> {
      const {promise} = this.requestAndGetId<Interfaces>(
        method,
        path,
        config,
        options,
      )
      return promise
    },

    async makeRequest<Interfaces extends IBaseApiRequestInterfaces>(
      method = 'get',
      path: string,
      config?: AxiosRequestConfig,
      requestId?: string,
    ): Promise<IToJson<Interfaces['response']>> {
      const authStore = useAuthStore()
      if (!config) {
        config = {}
      }
      const token = await authStore.getToken()
      const currentLinkId = authStore.currentLinkId
      const runtimeEnv = getRuntimeEnv()
      const axiosRequestConfig = {
        method,
        url: `${runtimeEnv.ApiHost}${path}`,
        ...(config ? config : {}),
        headers: {
          ...(token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : {}),
          ...(currentLinkId
            ? {
              'X-Current-Link-Id': currentLinkId,
            }
            : {}),
          ...(isGodModeEnabled()
            ? {
              'X-God-Mode': '1',
            }
            : {}),
          'X-Request-Compression': runtimeEnv.shouldRequestCompression ? 'true' : 'false',
          ...(requestId
            ? {
              [REQUEST_ID_HEADER]: requestId,
            }
            : {}),
          ...config.headers,
        },
      }
      const nativeUpload = extractNativeUploadRequest(axiosRequestConfig.url, config)
      if (nativeUpload) {
        // Serialized native uploads can start long after being dispatched, so
        // auth headers are rebuilt with a fresh token at transfer start.
        const buildNativeUploadHeaders = async (): Promise<Record<string, string>> => {
          const freshToken = await authStore.getToken()
          const freshLinkId = authStore.currentLinkId
          return {
            ...(freshToken
              ? {
                Authorization: `Bearer ${freshToken}`,
              }
              : {}),
            ...(freshLinkId
              ? {
                'X-Current-Link-Id': freshLinkId,
              }
              : {}),
            ...(isGodModeEnabled()
              ? {
                'X-God-Mode': '1',
              }
              : {}),
            ...(requestId
              ? {
                [REQUEST_ID_HEADER]: requestId,
              }
              : {}),
            ...Object.fromEntries(Object.entries(config.headers ?? {})
              .filter(([key]) => key.toLowerCase() !== 'content-type')
              .map(([key, value]) => [key, String(value)])),
          }
        }
        return await runNativeUploadRequest<IToJson<Interfaces['response']>>(
          nativeUpload,
          buildNativeUploadHeaders,
          config.onUploadProgress,
        )
      }
      const axiosResponse = await axios(axiosRequestConfig)
      if(config.responseType === 'blob' || config.responseType === 'arraybuffer') {
        return axiosResponse.data as IToJson<Interfaces['response']>
      }
      const res = axiosResponse.data
      let resultData
      if(ENV.shouldRequestCompression) {
        const decompressedData = jsonCompressionService.decompress<{
        success: boolean
        data: IToJson<Interfaces['response']>
      }>(res)
        resultData = decompressedData.data
      } else {
        resultData = res.data
      }
      return resultData
    },
  },
})
